import { queryOptions } from '@tanstack/react-query'
import { apiGet, unwrap } from '@/shared/api'
import type {
  PersonReport,
  ProjectReport,
  Rollup,
  Running,
  TeamReport,
  TimeEntry,
} from '../model/schema'
import {
  type PersonReportDto,
  type ProjectReportDto,
  type RollupDto,
  type RunningDto,
  type TeamReportDto,
  type TimeEntryDto,
  toPersonReport,
  toProjectReport,
  toRollup,
  toRunning,
  toTeamReport,
  toTimeEntry,
} from './mappers'

export const timeKeys = {
  all: ['time'] as const,
  rollup: (taskId: string) => [...timeKeys.all, 'rollup', taskId] as const,
  entries: (taskId: string) => [...timeKeys.all, 'entries', taskId] as const,
  running: () => [...timeKeys.all, 'running'] as const,
}

// «Что в работе сейчас» у меня — список: вести несколько задач разом легально
// (уникальность running на бэке — на задачу, не на человека), и бар обязан
// показывать их все. Свежие первыми. Тик — клиентский от startedAt; на возврате
// во вкладку освежаем (защита от дрейфа).
export const runningQueryOptions = queryOptions({
  queryKey: timeKeys.running(),
  queryFn: async (): Promise<Running[]> => {
    const list = unwrap<RunningDto[]>(await apiGet('/time/running')) ?? []
    return list.map(toRunning)
  },
  refetchOnWindowFocus: true,
})

export function taskRollupQueryOptions(taskId: string) {
  return queryOptions({
    queryKey: timeKeys.rollup(taskId),
    queryFn: async (): Promise<Rollup> =>
      toRollup(unwrap<RollupDto>(await apiGet(`/tasks/${taskId}/time`))),
    // Роллап освежается на возврате во вкладку — защита от дрейфа за ночь.
    refetchOnWindowFocus: true,
  })
}

export function taskEntriesQueryOptions(taskId: string) {
  return queryOptions({
    queryKey: timeKeys.entries(taskId),
    queryFn: async (): Promise<TimeEntry[]> => {
      const list = unwrap<TimeEntryDto[]>(await apiGet(`/tasks/${taskId}/time/entries`)) ?? []
      return list.map(toTimeEntry)
    },
  })
}

// Идущий сейчас интервал на этой задаче (для живого таймера ● HH:MM:SS).
export function runningEntry(entries: TimeEntry[]): TimeEntry | undefined {
  return entries.find((e) => e.stoppedAt === null)
}

// Отчёты времени. from/to — YYYY-MM-DD (день, из URL); ключ держит их как есть.
export const reportKeys = {
  all: ['reports'] as const,
  project: (id: string, from: string, to: string) =>
    [...reportKeys.all, 'project', id, { from, to }] as const,
  person: (id: string, from: string, to: string) =>
    [...reportKeys.all, 'person', id, { from, to }] as const,
  team: (from: string, to: string) => [...reportKeys.all, 'team', { from, to }] as const,
}

// Период валиден, когда обе даты заданы и конец не раньше начала (лексикографика
// YYYY-MM-DD совпадает с хронологией). Невалидный/пустой период — enabled=false.
function periodValid(id: string, from: string, to: string): boolean {
  return Boolean(id) && Boolean(from) && Boolean(to) && from <= to
}

export function projectReportQueryOptions(id: string, from: string, to: string) {
  return queryOptions({
    queryKey: reportKeys.project(id, from, to),
    queryFn: async (): Promise<ProjectReport> =>
      toProjectReport(
        unwrap<ProjectReportDto>(
          // Период — календарные дни `YYYY-MM-DD`, оба конца включительно:
          // границы моментов из них считает бэк в зоне организации.
          await apiGet(`/reports/project/${id}`, { from, to }),
        ),
      ),
    enabled: periodValid(id, from, to),
  })
}

// Команда за период: строка на каждого сотрудника. Объекта в запросе нет —
// валиден сам период.
export function teamReportQueryOptions(from: string, to: string) {
  return queryOptions({
    queryKey: reportKeys.team(from, to),
    queryFn: async (): Promise<TeamReport> =>
      toTeamReport(unwrap<TeamReportDto>(await apiGet('/reports/team', { from, to }))),
    enabled: Boolean(from) && Boolean(to) && from <= to,
  })
}

export function personReportQueryOptions(id: string, from: string, to: string) {
  return queryOptions({
    queryKey: reportKeys.person(id, from, to),
    queryFn: async (): Promise<PersonReport> =>
      toPersonReport(unwrap<PersonReportDto>(await apiGet(`/reports/person/${id}`, { from, to }))),
    enabled: periodValid(id, from, to),
  })
}
